// frontend/src/components/CaptionGenerator.js
//
// The caption generator embedded on /tools/instagram-caption-generator and
// /tools/tiktok-caption-generator.
//
// MEASURED BEFORE BUILDING (2026-08-15/16): `instagram caption generator` is
// 18,000 searches across five phrasings at KD 10-17, CPC $4.55, and the SERP
// carries NO AI Overview — unlike every informational query checked so far. The
// field (Nuelink, Canva, The Social Cat, Hootsuite, Pallyy, OneUp) is pure-AI
// with no data moat.
//
// TWO DIFFERENTIATORS, BOTH VISIBLE IN THE UI
//
//   1. THE TRUNCATION PREVIEW. Instagram shows ~125 characters before "... more";
//      LinkedIn ~210. Every competing tool hands you a caption with no indication
//      of where it gets cut. This renders the cutoff inline — the visible half in
//      full contrast, the hidden half dimmed — so you can see whether your hook
//      survives. That is the whole game on a feed, and nothing else on this SERP
//      does it.
//
//   2. ONE POST, EVERY NETWORK. Competing tools return a single caption. Writing
//      once and adapting per network IS the Greatly Social product, so the tool
//      demonstrates it rather than describing it.

import React, { useState } from 'react'
import { Row, Col, Form, Button, Spinner, Badge } from 'react-bootstrap'
import { Stars, Clipboard, ClipboardCheck, ExclamationTriangleFill, EyeSlash } from 'react-bootstrap-icons'

const PLATFORMS = [
  ['instagram', 'Instagram'], ['tiktok', 'TikTok'], ['linkedin', 'LinkedIn'],
  ['x', 'X'], ['facebook', 'Facebook'], ['threads', 'Threads'], ['youtube', 'YouTube'],
]
const TONES = [
  ['friendly', 'Friendly'], ['professional', 'Professional'], ['playful', 'Playful'],
  ['inspiring', 'Inspiring'], ['direct', 'Direct'], ['funny', 'Funny'],
]

/** Split a caption at the platform's truncation point, counting by code point. */
function splitAtCutoff (text, visible) {
  const chars = [...text]
  if (!visible || chars.length <= visible) return [text, '']
  return [chars.slice(0, visible).join(''), chars.slice(visible).join('')]
}

export default function CaptionGenerator ({ platform = 'instagram' }) {
  const [form, setForm] = useState({
    topic: '', platform, tone: 'friendly', hashtags: true, emoji: true,
  })
  const [result, setResult]   = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [capped, setCapped]   = useState(false)
  const [copied, setCopied]   = useState('')

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }))

  const submit = async e => {
    e.preventDefault()
    if (!form.topic.trim()) { setError('Tell us what the post is about first.'); return }
    setLoading(true); setError(''); setResult(null); setCopied('')
    try {
      const r = await fetch('/api/public/ai/caption', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      })
      const data = await r.json()
      if (!r.ok) { setError(data.error || 'Something went wrong.'); setCapped(!!data.limitReached); return }
      setResult(data)
    } catch {
      setError('Could not reach the server. Try again in a moment.')
    } finally { setLoading(false) }
  }

  const copy = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(id); setTimeout(() => setCopied(''), 2200)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = text; document.body.appendChild(ta); ta.select()
      try { document.execCommand('copy'); setCopied(id); setTimeout(() => setCopied(''), 2200) } catch { /* ignore */ }
      document.body.removeChild(ta)
    }
  }

  return (
    <div className='p-4 p-md-5 rounded-3 shadow-card' style={{ background: '#fff', border: '1px solid #e5e7eb' }}>
      <h2 className='fw-bold h4 mb-2'>
        <Stars className='text-primary me-2' />
        Write your caption
      </h2>
      <p className='text-muted mb-4'>
        Get captions for your post — and the same post rewritten for every other network.
        You will see exactly where each one gets cut off in the feed. Free, no sign-up.
      </p>

      <Form onSubmit={submit}>
        <Form.Group className='mb-3'>
          <Form.Label className='fw-semibold small'>
            What is the post about? <span className='text-danger'>*</span>
          </Form.Label>
          <Form.Control
            as='textarea' rows={2} maxLength={500}
            placeholder='e.g. we just launched an oat milk flat white at our Leeds coffee shop, made with local oats'
            value={form.topic} onChange={e => set('topic', e.target.value)}
          />
          <Form.Text className='text-muted' style={{ fontSize: 12 }}>
            Details make the difference — what it is, who it is for, what is different about it.
          </Form.Text>
        </Form.Group>

        <Row className='g-3 mb-3'>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Network</Form.Label>
            <Form.Select value={form.platform} onChange={e => set('platform', e.target.value)}>
              {PLATFORMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Form.Select>
          </Col>
          <Col md={4}>
            <Form.Label className='fw-semibold small'>Tone</Form.Label>
            <Form.Select value={form.tone} onChange={e => set('tone', e.target.value)}>
              {TONES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </Form.Select>
          </Col>
          <Col md={4} className='d-flex align-items-end'>
            <div className='d-flex gap-3 pb-1'>
              <Form.Check type='switch' id='cap-hash' label='Hashtags'
                          checked={form.hashtags} onChange={e => set('hashtags', e.target.checked)} />
              <Form.Check type='switch' id='cap-emoji' label='Emoji'
                          checked={form.emoji} onChange={e => set('emoji', e.target.checked)} />
            </div>
          </Col>
        </Row>

        <Button type='submit' variant='primary' size='lg' disabled={loading}>
          {loading
            ? <><Spinner size='sm' animation='border' className='me-2' />Writing…</>
            : 'Write my captions'}
        </Button>
        {!result && <span className='text-muted small ms-3'>Free · no sign-up · every network at once</span>}
      </Form>

      {error && (
        <div className='mt-4 p-3 rounded-3 d-flex gap-2'
             style={{ background: capped ? '#eff6ff' : '#f8ecE8', border: `1px solid ${capped ? '#bfdbfe' : '#e8c4b8'}` }}>
          <ExclamationTriangleFill className={capped ? 'text-primary mt-1' : 'text-danger mt-1'} />
          <div>
            <div className='small'>{error}</div>
            {capped && (
              <Button size='sm' variant='primary' href='/register' className='mt-2'>
                Start using Greatly Social free
              </Button>
            )}
          </div>
        </div>
      )}

      {result && (
        <div className='mt-4'>
          {result.visible && (
            <div className='p-3 rounded-3 mb-3 d-flex gap-2 align-items-start'
                 style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}>
              <EyeSlash className='text-primary mt-1 flex-shrink-0' />
              <div className='small'>
                {result.label} shows roughly the first <strong>{result.visible} characters</strong> before
                cutting the caption off with “… more”. Everything dimmed below is hidden until
                someone taps. Check your hook survives.
              </div>
            </div>
          )}

          {/* YouTube only — the title is a separate field with its own, tighter
              cut-off (100 allowed, ~70 shown in search). */}
          {result.titles?.length > 0 && (
            <>
              <h3 className='fw-bold h6 text-uppercase text-muted mb-1' style={{ letterSpacing: '.05em' }}>
                Titles
              </h3>
              <p className='text-muted small mb-3'>
                YouTube allows {result.titleMax} characters but shows about {result.titleVisible} in
                search — anything past the dimmed point is cut.
              </p>
              {result.titles.map((t, i) => {
                const [shown, hidden] = splitAtCutoff(t.text, result.titleVisible)
                const id = `title-${i}`
                return (
                  <div key={id} className='mb-2 p-3 rounded-3 d-flex align-items-center gap-3'
                       style={{ border: '1px solid #e5e7eb' }}>
                    <div className='flex-grow-1' style={{ wordBreak: 'break-word' }}>
                      <span className='fw-semibold'>{shown}</span>
                      {hidden && (
                        <>
                          <span className='fw-semibold text-muted' style={{ opacity: 0.42 }}>{hidden}</span>
                          <span className='text-primary small fw-semibold'> …</span>
                        </>
                      )}
                    </div>
                    <span className='small text-muted flex-shrink-0' style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {t.length}/{result.titleMax}
                    </span>
                    <Button size='sm' variant={copied === id ? 'success' : 'outline-secondary'}
                            onClick={() => copy(t.text, id)} className='flex-shrink-0'>
                      {copied === id ? <ClipboardCheck /> : <Clipboard />}
                    </Button>
                  </div>
                )
              })}
            </>
          )}

          <h3 className='fw-bold h6 text-uppercase text-muted mb-3 mt-4' style={{ letterSpacing: '.05em' }}>
            {result.titles?.length ? 'Descriptions' : `For ${result.label}`}
          </h3>

          {result.options.map((o, i) => {
            const [shown, hidden] = splitAtCutoff(o.text, result.visible)
            const id = `opt-${i}`
            return (
              <div key={i} className='mb-3 p-3 rounded-3' style={{ border: '1px solid #e5e7eb' }}>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                  <span>{shown}</span>
                  {hidden && (
                    <>
                      <span className='text-muted' style={{ opacity: 0.42 }}>{hidden}</span>
                      <span className='text-primary small fw-semibold'> … more</span>
                    </>
                  )}
                </div>
                <div className='d-flex align-items-center gap-3 mt-2'>
                  <span className='small text-muted' style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {o.length} / {result.max}
                  </span>
                  {result.visible && o.length > result.visible && (
                    <Badge bg='light' text='dark' className='fw-normal'>
                      {o.length - result.visible} characters hidden
                    </Badge>
                  )}
                  <Button size='sm' variant={copied === id ? 'success' : 'outline-secondary'}
                          onClick={() => copy(o.text, id)} className='ms-auto'>
                    {copied === id ? <><ClipboardCheck className='me-1' />Copied</> : <><Clipboard className='me-1' />Copy</>}
                  </Button>
                </div>
              </div>
            )
          })}

          {/* THE DIFFERENTIATOR — the same post, rewritten per network. */}
          {result.perNetwork?.length > 0 && (
            <>
              <h3 className='fw-bold h6 text-uppercase text-muted mt-4 mb-1' style={{ letterSpacing: '.05em' }}>
                The same post, rewritten for your other networks
              </h3>
              <p className='text-muted small mb-3'>
                Not the same text pasted everywhere — each one rewritten for that network's length and register.
              </p>
              {result.perNetwork.map(n => {
                const [shown, hidden] = splitAtCutoff(n.text, n.visible)
                const id = `net-${n.network}`
                return (
                  <div key={n.network} className='mb-3 p-3 rounded-3' style={{ border: '1px solid #e5e7eb', background: '#fafafa' }}>
                    <div className='fw-semibold small mb-1'>{n.label}</div>
                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.5 }}>
                      <span>{shown}</span>
                      {hidden && (
                        <>
                          <span className='text-muted' style={{ opacity: 0.42 }}>{hidden}</span>
                          <span className='text-primary small fw-semibold'> … more</span>
                        </>
                      )}
                    </div>
                    <div className='d-flex align-items-center gap-3 mt-2'>
                      <span className='small text-muted' style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {n.length} / {n.max}
                      </span>
                      <Button size='sm' variant={copied === id ? 'success' : 'outline-secondary'}
                              onClick={() => copy(n.text, id)} className='ms-auto'>
                        {copied === id ? <><ClipboardCheck className='me-1' />Copied</> : <><Clipboard className='me-1' />Copy</>}
                      </Button>
                    </div>
                  </div>
                )
              })}
            </>
          )}

          <div className='p-4 rounded-3 mt-4' style={{ background: 'linear-gradient(135deg,#1d4ed8,#2563eb)', color: '#fff' }}>
            <h3 className='fw-bold h5 mb-2'>You just wrote six captions. Now they need posting.</h3>
            <p className='mb-3' style={{ color: 'rgba(255,255,255,.88)' }}>
              At Greatly Social you can keep the per-network versions on one post and schedule
              the lot in a single pass — which is exactly what this tool just did, inside the product.
            </p>
            <Button variant='light' href='/register' className='fw-semibold me-2 mb-2'>
              Start using Greatly Social free
            </Button>
            <Button variant='outline-light' href='/best-time-to-post' className='mb-2'>
              Find your best times
            </Button>
          </div>

          {typeof result.remaining === 'number' && (
            <p className='text-muted small mt-3 mb-0'>
              {result.remaining > 0
                ? `${result.remaining} more free ${result.remaining === 1 ? 'set' : 'sets'} this session.`
                : 'That was your last free set this session.'}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
